import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { ButtonIcon, type IconName } from "@orgmym/leeloo-core";

import { useIsMobile } from "@/features/chat/shared/hooks";
import {
  MediaMenu,
  MediaMenuButton,
  MessageInput,
  VoiceMessageRecorder,
  type MediaMenuItem,
  type MessageInputHandle,
  type VoiceMessageRecorderProps
} from "@/features/chat/shared/components";
import * as styles from "./message-composer.css";

export interface MessageComposerProps
  extends Omit<React.ComponentPropsWithRef<"textarea">, "value"> {
  value?: string;
  isLoading?: boolean;
  onSend?: (content: string) => void;
  onLeftButtonClick?: () => void;
  leftButtonIcon?: IconName;
  uploadVoiceMessage?: VoiceMessageRecorderProps["uploadVoiceMessage"];
  sendVoiceMessage?: VoiceMessageRecorderProps["sendVoiceMessage"];
}

export interface MessageComposerHandle {
  focus: () => void;
  clear: () => void;
  getValue: () => string;
}

export const MessageComposer = React.forwardRef<
  MessageComposerHandle,
  MessageComposerProps
>(
  (
    {
      className,
      disabled = false,
      isLoading = false,
      value,
      onSend,
      onLeftButtonClick,
      leftButtonIcon = "add",
      uploadVoiceMessage,
      sendVoiceMessage,
      onChange,
      ...props
    },
    ref
  ) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [voiceActive, setVoiceActive] = useState(false);
    const [internalValue, setInternalValue] = useState("");
    const messageInputRef = useRef<MessageInputHandle>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    React.useImperativeHandle(
      ref,
      () => ({
        focus: () => messageInputRef.current?.focus(),
        clear: () => {
          messageInputRef.current?.clear();
          if (!isControlled) {
            setInternalValue("");
          }
        },
        getValue: () => messageInputRef.current?.getValue() || ""
      }),
      [isControlled]
    );

    useEffect(() => {
      if (!isMenuOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target as Node)
        ) {
          setIsMenuOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isMenuOpen]);

    const handleMenuToggle = useCallback(() => {
      setIsMenuOpen((prev) => !prev);
    }, []);

    const handleShowKeyboard = useCallback(() => {
      messageInputRef.current?.focus();
    }, []);

    const handleFocusChange = useCallback((focused: boolean) => {
      setHasFocus(focused);
    }, []);

    const handleSend = useCallback(
      (content: string) => {
        onSend?.(content);
        if (!isControlled) {
          setInternalValue("");
        }
        messageInputRef.current?.clear();
      },
      [onSend, isControlled]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!isControlled) {
          setInternalValue(e.target.value);
        }
        onChange?.(e);
      },
      [isControlled, onChange]
    );

    const handleClear = useCallback(() => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      } else {
        messageInputRef.current?.clear();
        if (!isControlled) {
          setInternalValue("");
        }
        if (onChange) {
          const event = {
            target: { value: "" },
            currentTarget: { value: "" }
          } as React.ChangeEvent<HTMLTextAreaElement>;
          onChange(event);
        }
        messageInputRef.current?.focus();
      }
    }, [isMenuOpen, isControlled, onChange]);

    const menuItems: MediaMenuItem[] = useMemo(
      () => [
        {
          id: "private-media",
          icon: "add-picture" as IconName,
          labelKey: "creators_messaging_private_media_proposal_button",
          testId: "menu-item-private-media",
          onClick: onLeftButtonClick
        }
        // TODO: re-enable when implemented
        // {
        //   id: "quick-replies",
        //   icon: "reply-all" as IconName,
        //   labelKey: "creatorstools_content_link_four",
        //   testId: "menu-item-quick-replies"
        // },
        // {
        //   id: "private-show",
        //   icon: "camera",
        //   labelKey: "chat_thread_instant_live_title",
        //   testId: "menu-item-private-show"
        // }
      ],
      [onLeftButtonClick]
    );

    const handleMenuItemClick = useCallback((item: MediaMenuItem) => {
      setIsMenuOpen(false);
      item.onClick?.();
    }, []);

    const hasText = useMemo(
      () => typeof currentValue === "string" && currentValue.trim().length > 0,
      [currentValue]
    );
    const showClearButton = hasText || isMenuOpen;

    return (
      <div
        ref={containerRef}
        className={`${styles.container} ${className || ""}`}
        data-testid="message-composer"
      >
        {onLeftButtonClick && !voiceActive && (
          <div className={styles.iconButtonWrapper}>
            <MediaMenuButton
              isOpen={isMenuOpen}
              isMobile={isMobile}
              disabled={disabled}
              defaultIcon={leftButtonIcon}
              onToggle={handleMenuToggle}
              onShowKeyboard={handleShowKeyboard}
            >
              {!isMobile && (
                <MediaMenu
                  items={menuItems}
                  onItemClick={handleMenuItemClick}
                  isMobile={false}
                  isOpen={isMenuOpen}
                />
              )}
            </MediaMenuButton>
          </div>
        )}

        {!voiceActive && (
          <div className={styles.inputContainer}>
            <MessageInput
              ref={messageInputRef}
              value={currentValue}
              disabled={disabled}
              isLoading={isLoading}
              onSend={handleSend}
              onChange={handleChange}
              onFocusChange={handleFocusChange}
              {...props}
            />

            {showClearButton && (
              <ButtonIcon
                iconName="close"
                iconSize="medium"
                variant="simple"
                size="medium"
                onPress={handleClear}
                className={styles.clearButton}
                disabled={disabled || isLoading}
                aria-label={isMenuOpen ? "Close menu" : "Clear message"}
                data-testid="message-clear-button"
              />
            )}
          </div>
        )}

        {uploadVoiceMessage && sendVoiceMessage && (
          <VoiceMessageRecorder
            disabled={disabled || isLoading}
            uploadVoiceMessage={uploadVoiceMessage}
            sendVoiceMessage={sendVoiceMessage}
            onActiveChange={setVoiceActive}
          />
        )}

        {isMobile && !voiceActive && (
          <MediaMenu
            items={menuItems}
            onItemClick={handleMenuItemClick}
            isMobile={true}
            isOpen={isMenuOpen}
            hasFocus={hasFocus}
          />
        )}
      </div>
    );
  }
);

MessageComposer.displayName = "MessageComposer";
